<template>
  <div>
    <Breadcrumbs main="Admin" title="Users" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">Firm Users</h5>
                <span class="text-muted">Manage staff accounts and access permissions</span>
              </div>
              <div class="d-flex gap-2">
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  :disabled="loading"
                  @click="loadUsers"
                >
                  <vue-feather type="refresh-cw" class="me-1" />
                  Refresh
                </button>
                <button
                  v-if="canAdd"
                  class="btn btn-primary"
                  type="button"
                  @click="openCreate"
                >
                  <vue-feather type="plus" class="me-1" />
                  Add User
                </button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="error" class="alert alert-danger" role="alert">
                {{ error }}
              </div>
              <div v-if="loading" class="text-center py-4 text-muted">
                Loading users...
              </div>
              <div v-else-if="users.length === 0" class="text-center py-4 text-muted">
                No users found.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Added</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="user in users" :key="user.userId">
                      <td>{{ user.userId }}</td>
                      <td>
                        <strong>{{ user.fullName }}</strong>
                        <span
                          v-if="user.userId === currentUser?.userId"
                          class="badge badge-light-primary ms-1"
                        >
                          You
                        </span>
                      </td>
                      <td>{{ user.email }}</td>
                      <td>{{ user.phoneNo || '—' }}</td>
                      <td>
                        <span class="badge badge-light-secondary">{{ user.userType || '—' }}</span>
                      </td>
                      <td>
                        <span
                          v-if="user.deleted"
                          class="badge badge-danger"
                        >
                          Deleted
                        </span>
                        <span
                          v-else
                          class="badge"
                          :class="user.active ? 'badge-success' : 'badge-secondary'"
                        >
                          {{ user.active ? 'Active' : 'Inactive' }}
                        </span>
                      </td>
                      <td>{{ formatDate(user.addedOn) }}</td>
                      <td class="text-end">
                        <button
                          v-if="canEdit"
                          class="btn btn-sm btn-outline-primary me-1"
                          type="button"
                          title="Edit"
                          @click="openEdit(user)"
                        >
                          <vue-feather type="edit-2" />
                        </button>
                        <button
                          v-if="canEdit && user.active && !user.deleted && user.userId !== currentUser?.userId"
                          class="btn btn-sm btn-outline-warning me-1"
                          type="button"
                          title="Deactivate"
                          @click="confirmDeactivate(user)"
                        >
                          <vue-feather type="user-x" />
                        </button>
                        <button
                          v-if="canDelete && !user.deleted && user.userId !== currentUser?.userId"
                          class="btn btn-sm btn-outline-danger"
                          type="button"
                          title="Delete"
                          @click="confirmDelete(user)"
                        >
                          <vue-feather type="trash-2" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="showFormModal"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
          <div class="modal-content">
            <form @submit.prevent="submitForm">
              <div class="modal-header">
                <h5 class="modal-title">{{ isEditing ? 'Edit User' : 'Add User' }}</h5>
                <button type="button" class="btn-close" aria-label="Close" @click="closeFormModal" />
              </div>
              <div class="modal-body">
                <div v-if="formError" class="alert alert-danger" role="alert">
                  {{ formError }}
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Full name <span class="text-danger">*</span></label>
                      <input v-model="form.fullName" class="form-control" type="text" required>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Email <span class="text-danger">*</span></label>
                      <input v-model="form.email" class="form-control" type="email" required>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Phone</label>
                      <input v-model="form.phoneNo" class="form-control" type="text">
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Role <span class="text-danger">*</span></label>
                      <select v-model="form.userType" class="form-control" required>
                        <option value="ADMIN">Admin</option>
                        <option value="STAFF">Staff</option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">
                        Password
                        <span v-if="!isEditing" class="text-danger">*</span>
                      </label>
                      <input
                        v-model="form.password"
                        class="form-control"
                        type="password"
                        :required="!isEditing"
                        :placeholder="isEditing ? 'Leave blank to keep current' : ''"
                        autocomplete="new-password"
                      >
                    </div>
                  </div>
                    <div v-if="isEditing" class="col-md-6">
                    <div class="form-group mb-2 d-flex align-items-end h-100 pb-1">
                      <div class="form-check form-switch">
                        <input
                          id="user-active"
                          v-model="form.active"
                          class="form-check-input"
                          type="checkbox"
                        >
                        <label class="form-check-label" for="user-active">Active</label>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="border-top pt-3 mt-1">
                  <div class="form-check mb-3">
                    <input
                      id="customize-privileges"
                      v-model="form.customizePrivileges"
                      class="form-check-input"
                      type="checkbox"
                    >
                    <label class="form-check-label" for="customize-privileges">
                      Customize entity permissions
                    </label>
                    <p class="text-muted f-12 mb-0 mt-1">
                      When unchecked, the user receives full access on all entities.
                    </p>
                  </div>

                  <div v-if="form.customizePrivileges" class="table-responsive">
                    <table class="table table-sm table-bordered mb-0">
                      <thead>
                        <tr>
                          <th>Entity</th>
                          <th class="text-center">View</th>
                          <th class="text-center">Add</th>
                          <th class="text-center">Edit</th>
                          <th class="text-center">Delete</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="entity in privilegeEntities" :key="entity">
                          <td>{{ formatEntity(entity) }}</td>
                          <td class="text-center">
                            <input
                              v-model="form.privileges[entity].canView"
                              class="form-check-input"
                              type="checkbox"
                            >
                          </td>
                          <td class="text-center">
                            <input
                              v-model="form.privileges[entity].canAdd"
                              class="form-check-input"
                              type="checkbox"
                            >
                          </td>
                          <td class="text-center">
                            <input
                              v-model="form.privileges[entity].canEdit"
                              class="form-check-input"
                              type="checkbox"
                            >
                          </td>
                          <td class="text-center">
                            <input
                              v-model="form.privileges[entity].canDelete"
                              class="form-check-input"
                              type="checkbox"
                            >
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeFormModal">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                  {{ saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create User') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div v-if="showFormModal" class="modal-backdrop fade show" @click="closeFormModal" />
    </teleport>

    <teleport to="body">
      <div
        v-if="userToDeactivate"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Deactivate User</h5>
              <button type="button" class="btn-close" aria-label="Close" @click="userToDeactivate = null" />
            </div>
            <div class="modal-body">
              <p class="mb-2">
                Deactivate <strong>{{ userToDeactivate.fullName }}</strong>?
              </p>
              <p class="text-muted mb-0 f-12">
                The user will not be able to sign in until reactivated.
              </p>
              <div v-if="actionError" class="alert alert-danger mt-3 mb-0" role="alert">
                {{ actionError }}
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="userToDeactivate = null">
                Cancel
              </button>
              <button type="button" class="btn btn-warning" :disabled="actionLoading" @click="submitDeactivate">
                {{ actionLoading ? 'Deactivating...' : 'Deactivate' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="userToDeactivate" class="modal-backdrop fade show" @click="userToDeactivate = null" />
    </teleport>

    <teleport to="body">
      <div
        v-if="userToDelete"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Delete User</h5>
              <button type="button" class="btn-close" aria-label="Close" @click="userToDelete = null" />
            </div>
            <div class="modal-body">
              <p class="mb-2">
                Soft-delete <strong>{{ userToDelete.fullName }}</strong>?
              </p>
              <p class="text-muted mb-0 f-12">
                The account will be deactivated and marked as deleted. The record is retained in the database.
              </p>
              <div v-if="actionError" class="alert alert-danger mt-3 mb-0" role="alert">
                {{ actionError }}
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="userToDelete = null">
                Cancel
              </button>
              <button type="button" class="btn btn-danger" :disabled="actionLoading" @click="submitDelete">
                {{ actionLoading ? 'Deleting...' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="userToDelete" class="modal-backdrop fade show" @click="userToDelete = null" />
    </teleport>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  listUsers,
  registerUser,
  updateUser,
  deactivateUser,
  deleteUser,
} from '@/services/userService'

const PRIVILEGE_ENTITIES = [
  'USER',
  'CLIENT',
  'SETTINGS',
  'MATTER',
  'COURT_DIARY',
  'DOCUMENT',
  'BILL',
  'INVOICE',
  'PAYMENT',
  'RECEIPT',
  'TRUST_ACCOUNT',
]

function defaultPrivileges() {
  const privileges = {}
  PRIVILEGE_ENTITIES.forEach((entity) => {
    privileges[entity] = {
      canView: true,
      canAdd: true,
      canEdit: true,
      canDelete: true,
    }
  })
  return privileges
}

function privilegesFromUser(userPrivileges) {
  const privileges = defaultPrivileges()
  if (!userPrivileges?.length) return privileges

  userPrivileges.forEach((priv) => {
    if (priv.entity && privileges[priv.entity]) {
      privileges[priv.entity] = {
        canView: !!priv.canView,
        canAdd: !!priv.canAdd,
        canEdit: !!priv.canEdit,
        canDelete: !!priv.canDelete,
      }
    }
  })
  return privileges
}

function hasUserPrivilege(user, action) {
  if (!user?.privileges?.length) return true
  const userPriv = user.privileges.find((p) => p.entity === 'USER')
  if (!userPriv) return false
  const map = {
    view: 'canView',
    add: 'canAdd',
    edit: 'canEdit',
    delete: 'canDelete',
  }
  return userPriv[map[action]] === true
}

const emptyForm = () => ({
  fullName: '',
  email: '',
  phoneNo: '',
  password: '',
  userType: 'STAFF',
  active: true,
  customizePrivileges: false,
  privileges: defaultPrivileges(),
})

export default {
  computed: {
    ...mapGetters('auth', ['currentUser', 'companyCode']),

    canView() {
      return hasUserPrivilege(this.currentUser, 'view')
    },

    canAdd() {
      return hasUserPrivilege(this.currentUser, 'add')
    },

    canEdit() {
      return hasUserPrivilege(this.currentUser, 'edit')
    },

    canDelete() {
      return hasUserPrivilege(this.currentUser, 'delete')
    },

    privilegeEntities() {
      return PRIVILEGE_ENTITIES
    },
  },

  data() {
    return {
      users: [],
      loading: false,
      error: '',
      showFormModal: false,
      isEditing: false,
      editingId: null,
      form: emptyForm(),
      formError: '',
      saving: false,
      userToDeactivate: null,
      userToDelete: null,
      actionError: '',
      actionLoading: false,
    }
  },

  created() {
    this.loadUsers()
  },

  methods: {
    async loadUsers() {
      this.loading = true
      this.error = ''

      try {
        const data = await listUsers()
        this.users = Array.isArray(data) ? data : []
      } catch (err) {
        if (err.status === 403) {
          this.error = 'You do not have permission to view users.'
        } else {
          this.error = err.message || 'Failed to load users'
        }
      } finally {
        this.loading = false
      }
    },

    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString()
    },

    formatEntity(entity) {
      return entity.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },

    openCreate() {
      this.isEditing = false
      this.editingId = null
      this.form = emptyForm()
      this.formError = ''
      this.showFormModal = true
    },

    openEdit(user) {
      this.isEditing = true
      this.editingId = user.userId
      this.form = {
        fullName: user.fullName || '',
        email: user.email || '',
        phoneNo: user.phoneNo || '',
        password: '',
        userType: user.userType || 'STAFF',
        active: user.active !== false,
        customizePrivileges: !!user.privileges?.length,
        privileges: privilegesFromUser(user.privileges),
      }
      this.formError = ''
      this.showFormModal = true
    },

    closeFormModal() {
      if (this.saving) return
      this.showFormModal = false
      this.formError = ''
    },

    buildPrivilegesPayload() {
      return PRIVILEGE_ENTITIES.map((entity) => ({
        entity,
        ...this.form.privileges[entity],
      }))
    },

    buildCreatePayload() {
      const payload = {
        companyCode: this.companyCode,
        fullName: this.form.fullName.trim(),
        email: this.form.email.trim(),
        password: this.form.password,
        userType: this.form.userType,
      }

      const phone = this.form.phoneNo?.trim()
      if (phone) payload.phoneNo = phone

      if (this.form.customizePrivileges) {
        payload.privileges = this.buildPrivilegesPayload()
      }

      return payload
    },

    buildUpdatePayload() {
      const payload = {
        fullName: this.form.fullName.trim(),
        email: this.form.email.trim(),
        userType: this.form.userType,
        active: this.form.active,
      }

      const phone = this.form.phoneNo?.trim()
      if (phone) payload.phoneNo = phone

      if (this.form.password) {
        payload.password = this.form.password
      }

      if (this.form.customizePrivileges) {
        payload.privileges = this.buildPrivilegesPayload()
      }

      return payload
    },

    async submitForm() {
      this.formError = ''
      this.saving = true

      try {
        if (this.isEditing) {
          await updateUser(this.editingId, this.buildUpdatePayload())
        } else {
          await registerUser(this.buildCreatePayload())
        }
        this.showFormModal = false
        await this.loadUsers()
      } catch (err) {
        this.formError = err.message || 'Request failed'
      } finally {
        this.saving = false
      }
    },

    confirmDeactivate(user) {
      this.userToDeactivate = user
      this.userToDelete = null
      this.actionError = ''
    },

    confirmDelete(user) {
      this.userToDelete = user
      this.userToDeactivate = null
      this.actionError = ''
    },

    async submitDeactivate() {
      this.actionError = ''
      this.actionLoading = true

      try {
        await deactivateUser(this.userToDeactivate.userId)
        this.userToDeactivate = null
        await this.loadUsers()
      } catch (err) {
        this.actionError = err.message || 'Failed to deactivate user'
      } finally {
        this.actionLoading = false
      }
    },

    async submitDelete() {
      this.actionError = ''
      this.actionLoading = true

      try {
        await deleteUser(this.userToDelete.userId)
        this.userToDelete = null
        await this.loadUsers()
      } catch (err) {
        this.actionError = err.message || 'Failed to delete user'
      } finally {
        this.actionLoading = false
      }
    },
  },
}
</script>
